import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { InactivityMessageTemplate } from '@coaching/api/db/schema.js';
import { ResponseButtonConfigSummary } from '../components/ResponseButtonConfigSummary';
import { trpc } from '../lib/trpc';
import { parseInactivityMessageTemplatesResult } from '../lib/inactivity';
import { useProject } from '../context';
import { tryHydrateResponseButtonConfig } from '../lib/responseButtons';

// Type for configuration parameters
interface ConfigParameter {
  name: string;
  type: string;
  required: boolean;
  description: string;
  default?: string | number | boolean;
}

/**
 * Bot detail page component.
 *
 * Displays detailed information about a specific bot configuration
 * including system prompt, onboarding flow, and tools configuration.
 */
export function BotDetailPage() {
  const { currentBotId: botId, isLoading: contextLoading } = useProject();
  const navigate = useNavigate();

  const botQuery = trpc.bots.get.useQuery(
    { id: botId! },
    { enabled: !!botId }
  );
  const responseButtonConfigResult = botQuery.data?.responseButtonConfig != null
    ? tryHydrateResponseButtonConfig(botQuery.data.responseButtonConfig)
    : null;

  if (contextLoading) {
    return <div style={styles.loading}>Loading...</div>;
  }

  if (!botId) {
    return (
      <div style={styles.error}>
        No bot selected. Please select a bot from the sidebar.
      </div>
    );
  }

  if (botQuery.isLoading) {
    return <div style={styles.loading}>Loading bot details...</div>;
  }

  if (botQuery.error) {
    return (
      <div style={styles.error}>
        Error loading bot: {botQuery.error.message}
      </div>
    );
  }

  const bot = botQuery.data;
  const inactivityTemplatesState = useMemo(() => {
    const result = parseInactivityMessageTemplatesResult(bot?.inactivityMessageTemplates);
    return {
      ...result,
      templates: [...result.templates].sort((a, b) => a.day - b.day),
    };
  }, [bot?.inactivityMessageTemplates]);

  if (!bot) {
    return (
      <div style={styles.error}>
        Bot not found
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button onClick={() => navigate('/bots')} style={styles.backButton}>
          &larr; Back to Bots
        </button>
        <h1 style={styles.title}>Bot Details</h1>
      </div>

      {/* Bot Header Card */}
      <div style={styles.card}>
        <div style={styles.botHeader}>
          <div style={styles.botIcon}>{bot.icon || '🤖'}</div>
          <div style={styles.botInfo}>
            <h2 style={styles.botName}>{bot.name}</h2>
            <p style={styles.botSlug}>/{bot.slug}</p>
          </div>
          <span
            style={{
              ...styles.statusBadge,
              ...getStatusStyle(bot.status ?? ''),
            }}
          >
            {bot.status ?? 'Unknown'}
          </span>
        </div>

        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Description</h3>
          <p style={styles.description}>
            {bot.description || 'No description provided'}
          </p>
        </div>

        <div style={styles.detailsGrid}>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Bot ID</span>
            <span style={styles.detailValue}>{bot.id}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Status</span>
            <span style={styles.detailValue}>{bot.status ?? 'Unknown'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Created At</span>
            <span style={styles.detailValue}>
              {bot.createdAt
                ? new Date(bot.createdAt).toLocaleString()
                : 'Unknown'}
            </span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Updated At</span>
            <span style={styles.detailValue}>
              {bot.updatedAt
                ? new Date(bot.updatedAt).toLocaleString()
                : 'Unknown'}
            </span>
          </div>
          {bot.color && (
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Color</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span
                  style={{
                    display: 'inline-block',
                    width: '20px',
                    height: '20px',
                    borderRadius: '4px',
                    backgroundColor: bot.color,
                    border: '1px solid #e2e8f0',
                  }}
                />
                <span style={styles.detailValue}>{bot.color}</span>
              </div>
            </div>
          )}
        </div>

        <div style={styles.actions}>
          <button
            onClick={() => navigate('/bot/edit')}
            style={styles.editButton}
          >
            Edit Bot
          </button>
          <button
            onClick={() => navigate('/onboarding/editor')}
            style={styles.onboardingButton}
          >
            Edit Onboarding Flow
          </button>
        </div>
      </div>

      {/* System Prompt Card */}
      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>System Prompt</h3>
        <pre style={styles.codeBlock}>
          {bot.systemPrompt || 'No system prompt configured'}
        </pre>
      </div>

      {/* Voice & Messaging Card */}
      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Voice & Messaging</h3>
        <div style={styles.detailsGrid}>
          <div style={{ ...styles.detailItem, gridColumn: '1 / -1' }}>
            <span style={styles.detailLabel}>Voice Style</span>
            <span style={styles.detailValue}>
              {bot.voiceStyle || 'Default'}
            </span>
          </div>
          <div style={{ ...styles.detailItem, gridColumn: '1 / -1' }}>
            <span style={styles.detailLabel}>Daily Message Template</span>
            {bot.dailyMessageTemplate ? (
              <pre style={styles.codeBlockSmall}>{bot.dailyMessageTemplate}</pre>
            ) : (
              <span style={styles.emptyText}>No daily message template configured</span>
            )}
          </div>
          <div style={{ ...styles.detailItem, gridColumn: '1 / -1' }}>
            <span style={styles.detailLabel}>Inactivity Message Templates</span>
            {inactivityTemplatesState.templates.length > 0 ? (
              <div style={styles.inactivityTemplatesList}>
                {inactivityTemplatesState.templates.map((template) => (
                  <div
                    key={`${template.day}-${template.templateName}-${template.languageCode ?? 'en'}`}
                    style={styles.inactivityTemplateCard}
                  >
                    <div style={styles.inactivityTemplateHeader}>
                      <span style={styles.inactivityDayBadge}>
                        Day {template.day}
                      </span>
                      <span style={styles.inactivityLanguageBadge}>
                        {(template.languageCode || 'en').toUpperCase()}
                      </span>
                    </div>
                    <code style={styles.inactivityTemplateName}>
                      {template.templateName}
                    </code>
                  </div>
                ))}
              </div>
            ) : inactivityTemplatesState.isInvalid ? (
              <span style={{ ...styles.emptyText, color: '#dc2626', fontStyle: 'normal' }}>
                Inactivity message template config is invalid. Review the stored JSON for malformed entries or duplicate days.
              </span>
            ) : (
              <span style={styles.emptyText}>No inactivity message templates configured</span>
            )}
          </div>
        </div>
      </div>

      {/* Onboarding Flow Card */}
      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Onboarding Flow</h3>
        {bot.onboardingFlow ? (
          <div>
            <OnboardingFlowDisplay flow={bot.onboardingFlow as OnboardingFlow} />
          </div>
        ) : (
          <p style={styles.emptyText}>No onboarding flow configured</p>
        )}
      </div>

      {/* Tools Configuration Card */}
      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Tools Configuration</h3>
        <div style={styles.section}>
          <h4 style={styles.subSectionTitle}>Enabled Tools</h4>
          {bot.enabledTools && Array.isArray(bot.enabledTools) && bot.enabledTools.length > 0 ? (
            <div style={styles.tagsContainer}>
              {(bot.enabledTools as string[]).map((tool, idx) => (
                <span key={idx} style={styles.tag}>{tool}</span>
              ))}
            </div>
          ) : (
            <p style={styles.emptyText}>No tools enabled</p>
          )}
        </div>
        {bot.toolConfigs && Object.keys(bot.toolConfigs as Record<string, unknown>).length > 0 ? (
          <div style={styles.section}>
            <h4 style={styles.subSectionTitle}>Tool Configurations</h4>
            <pre style={configStyles.jsonBlock}>
              {JSON.stringify(bot.toolConfigs, null, 2)}
            </pre>
          </div>
        ) : null}
      </div>

      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Response Buttons</h3>
        {bot.responseButtonConfig != null ? (
          responseButtonConfigResult?.success && responseButtonConfigResult.config ? (
            <ResponseButtonConfigSummary config={responseButtonConfigResult.config} />
          ) : (
            <div style={configStyles.errorBox}>
              <p style={configStyles.errorText}>
                Stored response button configuration could not be parsed by this admin build.
              </p>
              <pre style={configStyles.jsonBlock}>
                {JSON.stringify(bot.responseButtonConfig, null, 2)}
              </pre>
            </div>
          )
        ) : (
          <p style={styles.emptyText}>No response button configuration saved.</p>
        )}
      </div>

      {/* Configuration Parameters Card */}
      <ConfigurationParametersSection bot={bot} />

      {/* AI Model Overrides Card */}
      {(bot.overrideAiModel || bot.overrideAiMaxTokens || bot.overrideTrialDays) && (
        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>AI & Trial Overrides</h3>
          <div style={styles.detailsGrid}>
            {bot.overrideAiModel && (
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>AI Model</span>
                <span style={styles.detailValue}>{bot.overrideAiModel}</span>
              </div>
            )}
            {bot.overrideAiMaxTokens && (
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Max Tokens</span>
                <span style={styles.detailValue}>{bot.overrideAiMaxTokens.toLocaleString()}</span>
              </div>
            )}
            {bot.overrideTrialDays && (
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Trial Days</span>
                <span style={styles.detailValue}>{bot.overrideTrialDays} days</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Change History Card */}
      <ChangeHistorySection botId={bot.id} />

    </div>
  );
}

/**
 * Type for media configuration in onboarding steps.
 */
interface OnboardingMedia {
  type: 'image' | 'video' | 'audio' | 'document';
  source: string;
  caption?: string;
  filename?: string;
  mimeType?: string;
}

/**
 * Type for onboarding flow structure.
 * Matches backend onboardingStepSchema.
 */
interface OnboardingStep {
  id: string;
  type: 'text' | 'buttons' | 'list' | 'media' | 'time_picker' | 'payment' | 'informational';
  message: string;
  field?: string;
  validation?: {
    type: string;
    pattern?: string;
    min?: number;
    max?: number;
  };
  // Media step configuration
  media?: OnboardingMedia;
  // Button text for informational steps
  continueButtonText?: string;
}

interface OnboardingFlow {
  steps: OnboardingStep[];
  completionMessage: string;
  requireExternalOnboarding?: boolean;
  externalOnboardingMessage?: string;
}

/**
 * Component to display onboarding flow in a readable format.
 */
function OnboardingFlowDisplay({ flow }: { flow: OnboardingFlow }) {
  return (
    <div>
      {flow.steps && flow.steps.length > 0 ? (
        <div style={styles.stepsContainer}>
          {flow.steps.map((step, index) => (
            <div key={step.id} style={styles.stepCard}>
              <div style={styles.stepHeader}>
                <span style={styles.stepNumber}>{index + 1}</span>
                <span style={styles.stepType}>{step.type}</span>
                {step.field && (
                  <span style={styles.stepField}>Field: {step.field}</span>
                )}
              </div>
              <p style={styles.stepMessage}>{step.message}</p>
              {step.validation && (
                <div style={styles.stepValidation}>
                  <span style={styles.validationLabel}>Validation:</span>
                  <code style={styles.validationCode}>
                    {JSON.stringify(step.validation)}
                  </code>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p style={styles.emptyText}>No onboarding steps configured</p>
      )}
      {flow.completionMessage && (
        <div style={styles.completionMessage}>
          <span style={styles.detailLabel}>Completion Message</span>
          <p style={styles.stepMessage}>{flow.completionMessage}</p>
        </div>
      )}
      {flow.requireExternalOnboarding !== undefined && (
        <div style={styles.onboardingSetting}>
          <span style={styles.detailLabel}>Require External Onboarding</span>
          <p style={styles.stepMessage}>{flow.requireExternalOnboarding ? 'Yes' : 'No'}</p>
        </div>
      )}
      {flow.requireExternalOnboarding && flow.externalOnboardingMessage && (
        <div style={styles.onboardingSetting}>
          <span style={styles.detailLabel}>External Onboarding Message</span>
          <p style={styles.stepMessage}>{flow.externalOnboardingMessage}</p>
        </div>
      )}
    </div>
  );
}

/**
 * Configuration parameters section component.
 *
 * Displays bot configuration parameters with proper styling,
 * type badges, and required/optional indicators matching the Communication Plan design.
 */
function ConfigurationParametersSection({ bot }: { bot: {
  toolConfigs?: unknown;
  overrideAiModel?: string | null;
  overrideAiMaxTokens?: number | null;
  overrideTrialDays?: number | null;
  systemPrompt?: string | null;
  voiceStyle?: string | null;
  dailyMessageTemplate?: string | null;
  inactivityMessageTemplates?: InactivityMessageTemplate[] | null;
} }) {
  // Extract configuration parameters from bot data
  const parameters: ConfigParameter[] = useMemo(() => {
    const params: ConfigParameter[] = [];

    // Add system-level parameters
    params.push({
      name: 'systemPrompt',
      type: 'string',
      required: true,
      description: 'The main instruction set that defines bot behavior and personality',
      default: undefined,
    });

    params.push({
      name: 'voiceStyle',
      type: 'string',
      required: false,
      description: 'The tone and communication style used in responses',
      default: 'Default',
    });

    params.push({
      name: 'dailyMessageTemplate',
      type: 'string',
      required: false,
      description: 'Template for automated daily messages sent to users',
      default: undefined,
    });

    if (bot.inactivityMessageTemplates && bot.inactivityMessageTemplates.length > 0) {
      params.push({
        name: 'inactivityMessageTemplates',
        type: 'array',
        required: false,
        description: 'Exact-day WhatsApp templates used for inactivity follow-up messages',
        default: undefined,
      });
    }

    // Add AI model parameters if they exist
    if (bot.overrideAiModel !== undefined) {
      params.push({
        name: 'aiModel',
        type: 'string',
        required: false,
        description: 'Override the default AI model for this bot',
        default: 'gpt-4o',
      });
    }

    if (bot.overrideAiMaxTokens !== undefined) {
      params.push({
        name: 'maxTokens',
        type: 'number',
        required: false,
        description: 'Maximum number of tokens for AI responses',
        default: 4096,
      });
    }

    if (bot.overrideTrialDays !== undefined) {
      params.push({
        name: 'trialDays',
        type: 'number',
        required: false,
        description: 'Number of trial days for new users',
        default: 7,
      });
    }

    // Add tool config parameters if they exist
    if (bot.toolConfigs && typeof bot.toolConfigs === 'object') {
      const toolConfigsObj = bot.toolConfigs as Record<string, unknown>;
      Object.keys(toolConfigsObj).forEach((toolName) => {
        params.push({
          name: toolName,
          type: 'object',
          required: false,
          description: `Configuration for the ${toolName} tool`,
          default: undefined,
        });
      });
    }

    return params;
  }, [bot]);

  if (parameters.length === 0) {
    return null;
  }

  return (
    <div style={styles.card}>
      <div style={configStyles.sectionHeader}>
        <h3 style={styles.sectionTitle}>Configuration Parameters</h3>
        <span style={configStyles.paramCount}>
          {parameters.length} {parameters.length === 1 ? 'parameter' : 'parameters'}
        </span>
      </div>

      <div style={configStyles.parametersContainer}>
        {parameters.map((param, index) => (
          <div key={index} style={configStyles.parameterCard}>
            <div style={configStyles.parameterHeader}>
              <span style={configStyles.parameterName}>{param.name}</span>
              <span style={configStyles.typeBadge}>{param.type}</span>
              {param.required ? (
                <span style={configStyles.requiredBadge}>required</span>
              ) : (
                <span style={configStyles.optionalBadge}>optional</span>
              )}
            </div>
            <p style={configStyles.parameterDescription}>{param.description}</p>
            {param.default !== undefined && (
              <p style={configStyles.defaultValue}>
                Default: <code style={configStyles.defaultCode}>{String(param.default)}</code>
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Change History section component.
 *
 * Displays a timeline of changes made to the bot,
 * including the action, timestamp, and the admin who performed it.
 */
function ChangeHistorySection({ botId }: { botId: string }) {
  const historyQuery = trpc.bots.history.useQuery(
    { botId, limit: 20 },
    { enabled: !!botId }
  );

  if (historyQuery.isLoading) {
    return (
      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Change History</h3>
        <p style={styles.emptyText}>Loading history...</p>
      </div>
    );
  }

  if (historyQuery.error) {
    return (
      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Change History</h3>
        <p style={{ ...styles.emptyText, color: '#dc2626' }}>
          Error loading history: {historyQuery.error.message}
        </p>
      </div>
    );
  }

  const logs = historyQuery.data?.logs ?? [];

  if (logs.length === 0) {
    return (
      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Change History</h3>
        <p style={styles.emptyText}>No change history available</p>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={historyStyles.sectionHeader}>
        <h3 style={styles.sectionTitle}>Change History</h3>
        <span style={historyStyles.logCount}>
          {historyQuery.data?.pagination.total ?? 0} {(historyQuery.data?.pagination.total ?? 0) === 1 ? 'change' : 'changes'}
        </span>
      </div>

      <div style={historyStyles.timeline}>
        {logs.map((log, index) => (
          <div key={log.id} style={historyStyles.timelineItem}>
            <div style={historyStyles.timelineDot}>
              <span style={{
                ...historyStyles.actionIcon,
                ...getActionIconStyle(log.action),
              }}>
                {getActionIcon(log.action)}
              </span>
            </div>
            {index < logs.length - 1 && <div style={historyStyles.timelineLine} />}
            <div style={historyStyles.timelineContent}>
              <div style={historyStyles.timelineHeader}>
                <span style={{
                  ...historyStyles.actionBadge,
                  ...getActionBadgeStyle(log.action),
                }}>
                  {formatAction(log.action)}
                </span>
                <span style={historyStyles.timestamp}>
                  {log.createdAt ? new Date(log.createdAt).toLocaleString() : 'Unknown time'}
                </span>
              </div>
              <div style={historyStyles.adminInfo}>
                <span style={historyStyles.adminName}>
                  {log.admin.name || log.admin.email || 'Unknown admin'}
                </span>
                {log.admin.email && log.admin.name && (
                  <span style={historyStyles.adminEmail}>{log.admin.email}</span>
                )}
              </div>
              {log.metadata && (
                <ChangeMetadataDisplay metadata={log.metadata as AuditLogMetadata} action={log.action} />
              )}
            </div>
          </div>
        ))}
      </div>

      {historyQuery.data?.pagination.hasMore && (
        <div style={historyStyles.loadMoreContainer}>
          <p style={historyStyles.loadMoreText}>
            Showing {logs.length} of {historyQuery.data.pagination.total} changes
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Type for audit log metadata.
 */
interface AuditLogMetadata {
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  context?: Record<string, unknown>;
}

/**
 * Component to display change metadata in a readable format.
 */
function ChangeMetadataDisplay({ metadata, action }: { metadata: AuditLogMetadata; action: string }) {
  const { before, after, context } = metadata;

  // For duplicate action, show source info
  if (action === 'bot.duplicate' && context) {
    return (
      <div style={historyStyles.metadataContainer}>
        <p style={historyStyles.metadataText}>
          Duplicated from: <code style={historyStyles.metadataCode}>{String(context.sourceName)}</code>
          {context.sourceSlug ? (
            <span style={historyStyles.metadataSecondary}> (/{String(context.sourceSlug)})</span>
          ) : null}
        </p>
      </div>
    );
  }

  // For delete action, show context
  if (action === 'bot.delete' && context) {
    return (
      <div style={historyStyles.metadataContainer}>
        <p style={historyStyles.metadataText}>
          {context.permanent ? 'Permanently deleted' : 'Archived'}
        </p>
      </div>
    );
  }

  // For update action, show before/after changes
  if (action === 'bot.update' && before && after) {
    const changes = getChangedFields(before, after);
    if (changes.length === 0) return null;

    return (
      <div style={historyStyles.metadataContainer}>
        <p style={historyStyles.metadataLabel}>Changes:</p>
        <div style={historyStyles.changesList}>
          {changes.map((change, idx) => (
            <div key={idx} style={historyStyles.changeItem}>
              <span style={historyStyles.changeField}>{change.field}:</span>
              <span style={historyStyles.changeOld}>{formatValue(change.oldValue)}</span>
              <span style={historyStyles.changeArrow}>→</span>
              <span style={historyStyles.changeNew}>{formatValue(change.newValue)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // For create action, show created values
  if (action === 'bot.create' && after) {
    return (
      <div style={historyStyles.metadataContainer}>
        <p style={historyStyles.metadataText}>
          Created with name: <code style={historyStyles.metadataCode}>{String(after.name)}</code>
          {after.slug ? (
            <span style={historyStyles.metadataSecondary}> (/{String(after.slug)})</span>
          ) : null}
        </p>
      </div>
    );
  }

  return null;
}

/**
 * Get changed fields between before and after objects.
 */
function getChangedFields(before: Record<string, unknown>, after: Record<string, unknown>) {
  const changes: { field: string; oldValue: unknown; newValue: unknown }[] = [];

  const allKeys = new Set([...Object.keys(before), ...Object.keys(after)]);

  allKeys.forEach((key) => {
    const oldVal = before[key];
    const newVal = after[key];

    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      changes.push({
        field: key,
        oldValue: oldVal,
        newValue: newVal,
      });
    }
  });

  return changes;
}

/**
 * Format a value for display.
 */
function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '(empty)';
  if (typeof value === 'string') return value || '(empty)';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * Format action string for display.
 */
function formatAction(action: string): string {
  const actionMap: Record<string, string> = {
    'bot.create': 'Created',
    'bot.update': 'Updated',
    'bot.delete': 'Deleted',
    'bot.duplicate': 'Duplicated',
  };
  return actionMap[action] || action;
}

/**
 * Get icon for action type.
 */
function getActionIcon(action: string): string {
  const iconMap: Record<string, string> = {
    'bot.create': '+',
    'bot.update': '~',
    'bot.delete': '×',
    'bot.duplicate': '⧉',
  };
  return iconMap[action] || '•';
}

/**
 * Get style for action icon.
 */
function getActionIconStyle(action: string): React.CSSProperties {
  const styleMap: Record<string, React.CSSProperties> = {
    'bot.create': { backgroundColor: '#dcfce7', color: '#166534' },
    'bot.update': { backgroundColor: '#dbeafe', color: '#1d4ed8' },
    'bot.delete': { backgroundColor: '#fee2e2', color: '#991b1b' },
    'bot.duplicate': { backgroundColor: '#f3e8ff', color: '#7c3aed' },
  };
  return styleMap[action] || { backgroundColor: '#f1f5f9', color: '#64748b' };
}

/**
 * Get style for action badge.
 */
function getActionBadgeStyle(action: string): React.CSSProperties {
  const styleMap: Record<string, React.CSSProperties> = {
    'bot.create': { backgroundColor: '#dcfce7', color: '#166534' },
    'bot.update': { backgroundColor: '#dbeafe', color: '#1d4ed8' },
    'bot.delete': { backgroundColor: '#fee2e2', color: '#991b1b' },
    'bot.duplicate': { backgroundColor: '#f3e8ff', color: '#7c3aed' },
  };
  return styleMap[action] || { backgroundColor: '#f1f5f9', color: '#64748b' };
}

function getStatusStyle(status: string): React.CSSProperties {
  switch (status) {
    case 'active':
      return { backgroundColor: '#dcfce7', color: '#166534' };
    case 'inactive':
      return { backgroundColor: '#fee2e2', color: '#991b1b' };
    case 'archived':
      return { backgroundColor: '#f1f5f9', color: '#475569' };
    default:
      return { backgroundColor: '#f1f5f9', color: '#475569' };
  }
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: '900px',
  },
  header: {
    marginBottom: '24px',
  },
  backButton: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#3b82f6',
    fontSize: '14px',
    cursor: 'pointer',
    marginBottom: '12px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#1e293b',
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: '#64748b',
  },
  error: {
    padding: '16px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    marginBottom: '20px',
  },
  botHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
    paddingBottom: '24px',
    borderBottom: '1px solid #f1f5f9',
  },
  botIcon: {
    fontSize: '48px',
  },
  botInfo: {
    flex: 1,
  },
  botName: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '4px',
  },
  botSlug: {
    fontSize: '14px',
    color: '#94a3b8',
    fontFamily: 'monospace',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '6px 14px',
    borderRadius: '9999px',
    fontSize: '14px',
    fontWeight: '500',
  },
  section: {
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '16px',
  },
  subSectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#64748b',
    marginBottom: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  description: {
    fontSize: '16px',
    color: '#334155',
    lineHeight: '1.6',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '20px',
    marginBottom: '24px',
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  detailLabel: {
    fontSize: '12px',
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  detailValue: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#334155',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    borderTop: '1px solid #f1f5f9',
    paddingTop: '24px',
  },
  editButton: {
    padding: '10px 20px',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  onboardingButton: {
    padding: '10px 20px',
    backgroundColor: '#f0fdf4',
    color: '#16a34a',
    border: '1px solid #bbf7d0',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  emptyText: {
    color: '#94a3b8',
    fontSize: '14px',
    fontStyle: 'italic',
  },
  codeBlock: {
    backgroundColor: '#f8fafc',
    padding: '16px',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'monospace',
    lineHeight: '1.6',
    overflow: 'auto',
    maxHeight: '300px',
    color: '#334155',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    margin: 0,
  },
  codeBlockSmall: {
    backgroundColor: '#f8fafc',
    padding: '12px',
    borderRadius: '8px',
    fontSize: '13px',
    fontFamily: 'monospace',
    lineHeight: '1.5',
    overflow: 'auto',
    maxHeight: '150px',
    color: '#334155',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    margin: 0,
  },
  inactivityTemplatesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  inactivityTemplateCard: {
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  inactivityTemplateHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    flexWrap: 'wrap',
  },
  inactivityDayBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '9999px',
    backgroundColor: '#dbeafe',
    color: '#1d4ed8',
    fontSize: '12px',
    fontWeight: '600',
  },
  inactivityLanguageBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '9999px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    fontSize: '12px',
    fontWeight: '500',
  },
  inactivityTemplateName: {
    fontSize: '13px',
    color: '#334155',
    fontFamily: 'monospace',
    wordBreak: 'break-word',
  },
  jsonBlock: {
    backgroundColor: '#f8fafc',
    padding: '16px',
    borderRadius: '8px',
    fontSize: '13px',
    fontFamily: 'monospace',
    lineHeight: '1.5',
    overflow: 'auto',
    maxHeight: '300px',
    color: '#334155',
    margin: 0,
  },
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  tag: {
    display: 'inline-block',
    padding: '6px 12px',
    backgroundColor: '#e0f2fe',
    color: '#0369a1',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
  },
  stepsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  stepCard: {
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    padding: '16px',
    borderLeft: '4px solid #3b82f6',
  },
  stepHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px',
  },
  stepNumber: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    fontSize: '12px',
    fontWeight: '600',
  },
  stepType: {
    display: 'inline-block',
    padding: '4px 10px',
    backgroundColor: '#e0f2fe',
    color: '#0369a1',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: '500',
    textTransform: 'capitalize',
  },
  stepField: {
    fontSize: '12px',
    color: '#64748b',
    fontFamily: 'monospace',
    backgroundColor: '#f1f5f9',
    padding: '4px 8px',
    borderRadius: '4px',
  },
  stepMessage: {
    fontSize: '14px',
    color: '#334155',
    lineHeight: '1.5',
    margin: 0,
  },
  stepValidation: {
    marginTop: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  validationLabel: {
    fontSize: '12px',
    color: '#64748b',
    fontWeight: '500',
  },
  validationCode: {
    fontSize: '11px',
    backgroundColor: '#f1f5f9',
    padding: '4px 8px',
    borderRadius: '4px',
    fontFamily: 'monospace',
    color: '#475569',
  },
  completionMessage: {
    marginTop: '16px',
    padding: '16px',
    backgroundColor: '#dcfce7',
    borderRadius: '8px',
    borderLeft: '4px solid #22c55e',
  },
  onboardingSetting: {
    marginTop: '16px',
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    borderLeft: '4px solid #cbd5e1',
  },
};

// Change History styles
const historyStyles: Record<string, React.CSSProperties> = {
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  logCount: {
    fontSize: '12px',
    color: '#64748b',
    fontWeight: 500,
  },
  timeline: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  timelineItem: {
    position: 'relative',
    display: 'flex',
    gap: '16px',
    paddingBottom: '20px',
  },
  timelineDot: {
    position: 'relative',
    flexShrink: 0,
    zIndex: 1,
  },
  actionIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    fontSize: '14px',
    fontWeight: 600,
  },
  timelineLine: {
    position: 'absolute',
    left: '13px',
    top: '28px',
    bottom: '0',
    width: '2px',
    backgroundColor: '#e2e8f0',
  },
  timelineContent: {
    flex: 1,
    minWidth: 0,
  },
  timelineHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '4px',
    flexWrap: 'wrap',
  },
  actionBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: 500,
  },
  timestamp: {
    fontSize: '12px',
    color: '#94a3b8',
  },
  adminInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    flexWrap: 'wrap',
  },
  adminName: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#334155',
  },
  adminEmail: {
    fontSize: '12px',
    color: '#94a3b8',
  },
  metadataContainer: {
    marginTop: '8px',
    padding: '10px 12px',
    backgroundColor: '#f8fafc',
    borderRadius: '6px',
    borderLeft: '3px solid #e2e8f0',
  },
  metadataLabel: {
    fontSize: '11px',
    color: '#64748b',
    fontWeight: 500,
    marginBottom: '6px',
    margin: '0 0 6px 0',
  },
  metadataText: {
    fontSize: '13px',
    color: '#475569',
    margin: 0,
  },
  metadataCode: {
    fontFamily: 'monospace',
    backgroundColor: '#e2e8f0',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '12px',
  },
  metadataSecondary: {
    color: '#94a3b8',
    fontSize: '12px',
  },
  changesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  changeItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    flexWrap: 'wrap',
  },
  changeField: {
    fontWeight: 500,
    color: '#475569',
    fontFamily: 'monospace',
  },
  changeOld: {
    color: '#991b1b',
    backgroundColor: '#fee2e2',
    padding: '2px 6px',
    borderRadius: '4px',
    fontFamily: 'monospace',
    fontSize: '11px',
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  changeArrow: {
    color: '#94a3b8',
  },
  changeNew: {
    color: '#166534',
    backgroundColor: '#dcfce7',
    padding: '2px 6px',
    borderRadius: '4px',
    fontFamily: 'monospace',
    fontSize: '11px',
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  loadMoreContainer: {
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #f1f5f9',
    textAlign: 'center',
  },
  loadMoreText: {
    fontSize: '12px',
    color: '#94a3b8',
    margin: 0,
  },
};

// Configuration Parameters styles matching Communication Plan design
const configStyles: Record<string, React.CSSProperties> = {
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  paramCount: {
    fontSize: '11px',
    color: '#9B9285',
    fontWeight: 500,
  },
  parametersContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  parameterCard: {
    padding: '12px',
    background: 'rgba(93, 86, 78, 0.03)',
    borderRadius: '8px',
  },
  parameterHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '6px',
    flexWrap: 'wrap',
  },
  parameterName: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#3D3A36',
    fontFamily: 'monospace',
  },
  typeBadge: {
    padding: '2px 6px',
    borderRadius: '4px',
    background: 'rgba(93, 86, 78, 0.08)',
    fontSize: '9px',
    color: '#706A62',
    fontWeight: 500,
  },
  requiredBadge: {
    padding: '2px 6px',
    borderRadius: '4px',
    background: 'rgba(139, 90, 80, 0.1)',
    fontSize: '9px',
    color: '#8B5A50',
    fontWeight: 500,
  },
  optionalBadge: {
    padding: '2px 6px',
    borderRadius: '4px',
    background: 'rgba(90, 138, 94, 0.1)',
    fontSize: '9px',
    color: '#5A8A5E',
    fontWeight: 500,
  },
  parameterDescription: {
    fontSize: '11px',
    color: '#706A62',
    lineHeight: '1.5',
    margin: 0,
  },
  defaultValue: {
    fontSize: '10px',
    color: '#9B9285',
    marginTop: '4px',
    margin: '4px 0 0 0',
  },
  defaultCode: {
    fontFamily: 'monospace',
    background: 'rgba(93, 86, 78, 0.06)',
    padding: '1px 4px',
    borderRadius: '3px',
    fontSize: '9px',
  },
  jsonBlock: {
    backgroundColor: 'rgba(93, 86, 78, 0.03)',
    padding: '12px',
    borderRadius: '8px',
    fontSize: '11px',
    fontFamily: 'monospace',
    lineHeight: '1.5',
    overflow: 'auto',
    maxHeight: '200px',
    color: '#3D3A36',
    margin: 0,
    border: '1px solid rgba(93, 86, 78, 0.08)',
  },
  errorBox: {
    padding: '12px',
    borderRadius: '8px',
    backgroundColor: '#fff7ed',
    border: '1px solid #fdba74',
  },
  errorText: {
    margin: '0 0 12px 0',
    fontSize: '12px',
    lineHeight: '1.5',
    color: '#9a3412',
  },
};

export default BotDetailPage;
